import React, { useEffect } from "react";
import { Printer } from "lucide-react";
import { formatMoney, itName, itLineTotal } from "../../../lib/retailFormat";
import { lineSpiceFor as spiceOf } from "../../../lib/retailFormat";
import {
  orderPaymentBreakdown,
  externalPaidLabel,
  loyaltyRedeemedLabel,
} from "../../../lib/orderPaymentBreakdown";
import { fulfillmentPromise } from "../../../lib/retail/fulfillmentPresentation";
import TakeoutBadge from "../TakeoutBadge";

// Phase 3M — extracted verbatim from RetailCustomer.jsx::PrintReceiptOverlay.
// Behaviour, print CSS scoping, and testids identical.
export default function PrintReceipt({ tenant, catalog, order, onClose }) {
  // Order Discount (Sep 2026) — every figure is the order's own
  // persisted, server-computed value. The receipt used to prefer numbers
  // the browser stamped onto the order at checkout, which could disagree
  // with what was charged (and could never reflect a discount staff
  // applied afterwards). Nothing is recomputed here.
  const subtotal = Number(order.subtotal ?? 0);
  const couponDiscount = Number(order.discount ?? 0);
  const orderDiscount = Number(order.order_discount ?? 0);
  const tax      = Number(order.tax ?? 0);
  const delivery = Number(order.delivery ?? 0);
  const tip      = Number(order.tip ?? 0);
  const total    = Number(order.total ?? 0);
  const couponCode = order.coupon_code;
  const currency = catalog?.currency || "USD";
  // `null` for ASAP — the receipt then renders exactly as it does today.
  const promise = fulfillmentPromise(order);
  const now = new Date();

  // Loyalty display correction (Sep 2026) — a redemption is a tender, not
  // a discount. This receipt used to print `TOTAL $2.17` above `Paid via
  // CASH`, which told the customer they had handed over $2.17 in cash
  // when $1.00 of it had been settled in points. The order total stays
  // exactly as it was; the payment is now allocated below it.
  //
  // `total` is the same figure the TOTAL line renders, so
  // the allocation always reconciles with what is printed above it.
  // Orders with no loyalty tender produce `hasLoyalty === false` and this
  // receipt is byte-for-byte what it has always been.
  const breakdown = orderPaymentBreakdown({ total, loyalty: order.loyalty });
  const showLoyalty = !!(breakdown && breakdown.hasLoyalty);

  // Product tax treatment (Oct 2026) — grouping comes from the order's
  // own per-line records (`items[].taxable`, `tax_summary`).
  const taxSummary = order.tax_summary || {};
  const grouped = taxSummary.available === true;
  const taxableItems = (order.items || []).filter((it) => it.taxable === true);
  const nonTaxableItems = (order.items || []).filter((it) => it.taxable !== true);
  // One receipt line — shared by the grouped and flat layouts. A taxable
  // line on a grouped bill carries the "(T)" marker after its name.
  const renderItem = (it) => (
    <div key={it.item_id || it.id} className="flex justify-between gap-2">
      <span className="pr-2 flex items-center gap-1.5 min-w-0">
        <span className="truncate">{it.quantity}× {grouped && it.taxable ? `${itName(it)} (T)` : itName(it)}</span>
        {spiceOf(it) && (
          <span className="text-[10px] shrink-0" data-testid={`retail-receipt-spice-${it.item_id || it.id}`}>
            [{spiceOf(it).label.toUpperCase()}]
          </span>
        )}
        {it.item_takeout && (
          <TakeoutBadge testId={`retail-receipt-item-takeout-${it.item_id || it.id}`} />
        )}
      </span>
      <span className="shrink-0">{formatMoney(itLineTotal(it), currency)}</span>
    </div>
  );

  useEffect(() => {
    const t = setTimeout(() => { try { window.print(); } catch (_e) { /* print blocked */ } }, 250);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center px-4 py-8 overflow-y-auto print:bg-white print:p-0" data-testid="retail-print-receipt">
      <style>{`
        @media print {
          /* Prior implementation used visibility:hidden on body * which
             PRESERVES the box's layout space — so the kiosk's min-h-screen
             container behind the receipt still consumed 13 sheets of blank
             paper. Fix: use :has() + :not() to hide only elements outside
             the receipt subtree, and force every ancestor of the receipt
             into auto height so the print flow contains ONLY the receipt. */
          @page { size: auto; margin: 8mm; }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }
          /* Hide every element that is NOT the receipt, NOT a descendant
             of the receipt, and NOT an ancestor of the receipt. Compound
             :not() selector must be written on ONE line — a space between
             :not() clauses becomes a descendant combinator. */
          body *:not(:has([data-testid="retail-print-receipt"])):not([data-testid="retail-print-receipt"]):not([data-testid="retail-print-receipt"] *) { display: none !important; }
          /* Ancestors of the receipt: strip layout constraints so they
             don't force additional pages. */
          body :has([data-testid="retail-print-receipt"]) {
            height: auto !important;
            min-height: 0 !important;
            max-height: none !important;
            padding: 0 !important;
            margin: 0 !important;
            background: #fff !important;
            position: static !important;
            overflow: visible !important;
            display: block !important;
          }
          [data-testid="retail-print-receipt"] {
            position: static !important;
            inset: auto !important;
            padding: 0 !important;
            margin: 0 !important;
            background: #fff !important;
            height: auto !important;
            min-height: 0 !important;
            overflow: visible !important;
          }
          [data-testid="retail-print-receipt"] .print-hide { display: none !important; }
          .receipt-card {
            box-shadow: none !important;
            border: none !important;
            max-width: 100% !important;
            padding: 4mm !important;
            margin: 0 auto !important;
          }
        }
      `}</style>
      <div className="receipt-card relative z-10 w-full max-w-sm bg-white text-stone-900 rounded-xl shadow-2xl p-6 font-mono text-sm">
        <div className="text-center mb-3">
          <div className="font-serif text-xl">{tenant?.name || catalog?.name || "Our Kitchen"}</div>
          {tenant?.location && <div className="text-xs text-stone-500">{tenant.location}</div>}
          <div className="text-xs text-stone-500 mt-1">{now.toLocaleString()}</div>
        </div>
        <div className="border-t border-b border-dashed border-stone-300 py-2 my-2 text-xs text-stone-600">
          <div className="flex justify-between"><span>Order #</span><span className="font-bold text-stone-900">{order.order_code}</span></div>
          {order.customer?.name && <div className="flex justify-between"><span>Customer</span><span>{order.customer.name}</span></div>}
          {order.fulfillment?.mode && <div className="flex justify-between"><span>Type</span><span>{(catalog?.order_mode_labels || { dine_in: "Dine-In", takeout: "Takeout", pickup: "Pickup", delivery: "Local Delivery" })[order.fulfillment.mode] || order.fulfillment.mode}</span></div>}
          {order.fulfillment?.table_number && <div className="flex justify-between"><span>Table</span><span>{order.fulfillment.table_number}</span></div>}
          {/* Catalog Fulfillment Scheduling (Sep 2026) — the PROMISED
              fulfillment time, which is not the order-placed time above
              and is the one that matters operationally. Absent entirely
              for an ASAP order, so an ordinary receipt is unchanged. */}
          {promise && (
            <div className="flex justify-between" data-testid="receipt-scheduled-promise">
              <span>Scheduled</span>
              <span className="font-bold text-stone-900">
                {promise.dateLabel} · {promise.windowLabel}
              </span>
            </div>
          )}
        </div>
        {/* Product tax treatment (Oct 2026) — a customer bill groups
            TAXABLE / NON-TAXABLE items (taxable names marked "(T)") when
            the order carries per-line tax records; otherwise the flat
            list exactly as before. Each group keeps the order's own item
            order. */}
        {grouped ? (
          <div className="mt-2 space-y-2">
            {[["taxable", "TAXABLE ITEMS", taxableItems], ["non_taxable", "NON-TAXABLE ITEMS", nonTaxableItems]]
              .filter(([, , list]) => list.length > 0)
              .map(([key, label, list]) => (
                <div key={key} className="space-y-1" data-testid={`retail-receipt-group-${key}`}>
                  <div className="text-[11px] font-bold tracking-wide">{label}</div>
                  {list.map(renderItem)}
                </div>
              ))}
            {taxableItems.length > 0 && (
              <div className="text-[11px] text-stone-500" data-testid="retail-receipt-tax-legend">
                {taxSummary.legend || "(T) = Taxable item"}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-1 mt-2">
            {(order.items || []).map(renderItem)}
          </div>
        )}
        <div className="border-t border-dashed border-stone-300 mt-3 pt-2 space-y-0.5 text-xs">
          <div className="flex justify-between"><span>Subtotal</span><span>{formatMoney(subtotal, currency)}</span></div>
          {couponDiscount > 0 && (
            <div className="flex justify-between" data-testid="retail-receipt-coupon-discount">
              <span>{couponCode ? `Coupon ${couponCode}` : "Discount"}</span><span>− {formatMoney(couponDiscount, currency)}</span>
            </div>
          )}
          {orderDiscount > 0 && (
            <div className="flex justify-between" data-testid="retail-receipt-order-discount">
              <span>Order Discount</span><span>− {formatMoney(orderDiscount, currency)}</span>
            </div>
          )}
          {grouped && couponDiscount > 0 && orderDiscount > 0 && (
            <div className="flex justify-between" data-testid="retail-receipt-total-discounts">
              <span>Total discounts</span><span>− {formatMoney(taxSummary.total_discount, currency)}</span>
            </div>
          )}
          {grouped && (
            <>
              <div className="flex justify-between" data-testid="retail-receipt-taxable-subtotal">
                <span>Taxable subtotal</span><span>{formatMoney(taxSummary.taxable_subtotal, currency)}</span>
              </div>
              <div className="flex justify-between" data-testid="retail-receipt-non-taxable-subtotal">
                <span>Non-taxable subtotal</span><span>{formatMoney(taxSummary.non_taxable_subtotal, currency)}</span>
              </div>
            </>
          )}
          <div className="flex justify-between"><span>Tax</span><span>{formatMoney(tax, currency)}</span></div>
          {delivery > 0 && <div className="flex justify-between"><span>Delivery</span><span>{formatMoney(delivery, currency)}</span></div>}
          {tip > 0 && <div className="flex justify-between"><span>Tip</span><span>{formatMoney(tip, currency)}</span></div>}
          <div className="flex justify-between font-bold text-base border-t border-stone-400 mt-1 pt-1">
            <span>TOTAL</span><span>{formatMoney(total, currency)}</span>
          </div>
          {showLoyalty && (
            <>
              <div className="flex justify-between mt-1" data-testid="retail-receipt-loyalty-tender">
                <span>{loyaltyRedeemedLabel(breakdown.pointsRedeemed)}</span>
                <span>− {formatMoney(breakdown.loyaltyTenderCents / 100, currency)}</span>
              </div>
              <div className="flex justify-between font-semibold" data-testid="retail-receipt-external-paid">
                <span>{externalPaidLabel(order.payment_method)}</span>
                <span>{formatMoney(breakdown.externalCents / 100, currency)}</span>
              </div>
              <div className="flex justify-between" data-testid="retail-receipt-balance-due">
                <span>BALANCE DUE</span><span>{formatMoney(breakdown.balanceDueCents / 100, currency)}</span>
              </div>
            </>
          )}
          {order.payment_method && (
            <div className="flex justify-between text-stone-500 mt-1"><span>Paid via</span><span className="uppercase">{order.payment_method}</span></div>
          )}
        </div>
        <p className="text-center text-xs text-stone-500 mt-4">Thank you for your visit! 🙏</p>
        <div className="mt-5 grid grid-cols-2 gap-2 print-hide">
          <button onClick={() => { try { window.print(); } catch (_e) { /* print blocked */ } }} className="h-10 rounded-md bg-orange-500 text-white text-sm font-semibold inline-flex items-center justify-center gap-1.5" data-testid="retail-print-now-btn">
            <Printer className="w-4 h-4" /> Print
          </button>
          <button onClick={onClose} className="h-10 rounded-md border border-stone-300 text-sm" data-testid="retail-print-close">Close</button>
        </div>
      </div>
    </div>
  );
}
